import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@material-ui/core';
import { IList, tuple } from 'annotation-tools/utils';
import React, { FC, memo, MouseEvent, useMemo } from 'react';
import styled from 'styled-components';
import { colorDef } from '../../../constants/styles/color-def';
import { viewTexts } from '../../../constants/view-texts';
import { modelStatusViewValue } from '../../../functions/convert-to-view-value/model-status-view-value';
import { IModelType } from '../../../types/record/model';
import { CreateNewModelDialog } from '../../dialogs/create-new-model-dialog/create-new-model-dialog';
import { DeleteModelDialog } from '../../dialogs/delete-model-dialog/delete-model-dialog';
import { EditModelDialog } from '../../dialogs/edit-model-dialog/edit-model-dialog';
import { MuiButton } from '../../shared/form-element';
import { SearchBar } from '../../shared/form-element/mui-search-bar';
import { MuiPaper } from '../../shared/mui/mui-paper';
import { MuiTablePagination } from '../../shared/mui/mui-table-pagination';
import { MuiTableRow } from '../../shared/mui/mui-table-row';
import { Root } from '../../shared/styled/root';

const vt = viewTexts.pages.modelList;

const CreateButtonWrapper = styled.div`
  padding: 10px;
`;

const SearchBarWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const ButtonWrapper = styled.div`
  padding: 0 5px;
  display: inline-block;
`;

interface Props {
  searchString: string;
  onSearchStringChange: (value: string) => void;
  page: number;
  onPageChange: (value: number) => void;
  rowsPerPage: number;
  onRowsPerPageChange: (value: number) => void;
  numRows: number;
  companyId: number;
  modelListFiltered: IList<IModelType>;
  modelOnClick: (id: number) => void;
  createNewModelOnClick: () => void;
  createNewModelDialogOpen: boolean;
  createNewModelDialogOnClose: (reason: 'ok' | 'cancel' | '') => void;
  editModelOnClick: (model: IModelType) => void;
  editModelDialogIsOpen: boolean;
  editModelDialogOnClose: (reason: 'ok' | 'cancel' | '') => void;
  modelToEdit: IModelType | undefined;
  deleteModelOnClick: (model: IModelType) => void;
  deleteModelDialogIsOpen: boolean;
  deleteModelDialogOnClose: (reason: 'ok' | 'cancel' | '') => void;
  modelToDelete: IModelType | undefined;
}

export const ModelListView: FC<Props> = memo(
  ({
    searchString,
    onSearchStringChange,
    page,
    onPageChange,
    rowsPerPage,
    onRowsPerPageChange,
    numRows,
    companyId,
    modelListFiltered,
    modelOnClick,
    createNewModelOnClick,
    createNewModelDialogOpen,
    createNewModelDialogOnClose,
    editModelOnClick,
    editModelDialogIsOpen,
    editModelDialogOnClose,
    modelToEdit,
    deleteModelOnClick,
    deleteModelDialogIsOpen,
    deleteModelDialogOnClose,
    modelToDelete
  }: Readonly<Props>) => {
    const modelsWithHandler = useMemo(
      () =>
        modelListFiltered.map(model =>
          tuple(
            {
              id: model.id,
              name: model.name,
              numLabels: model.numLabels,
              status: modelStatusViewValue(model.status),
              dateCreatedString: new Date(model.dateCreated).toLocaleString(),
              dateUpdatedString: new Date(model.dateUpdated).toLocaleString(),
              canBeDeleted: model.canBeDeleted
            },
            () => modelOnClick(model.id),
            (event: MouseEvent) => {
              event.stopPropagation();
              editModelOnClick(model);
            },
            (event: MouseEvent) => {
              event.stopPropagation();
              deleteModelOnClick(model);
            }
          )
        ),
      [modelListFiltered, modelOnClick, editModelOnClick, deleteModelOnClick]
    );

    return (
      <Root>
        <SearchBarWrapper>
          <SearchBar
            placeholder={vt.searchBarPlaceholder}
            value={searchString}
            onInput={onSearchStringChange}
          />
          <CreateButtonWrapper>
            <MuiButton
              label={vt.buttons.create}
              onClick={createNewModelOnClick}
            />
          </CreateButtonWrapper>
        </SearchBarWrapper>

        <MuiPaper bgColor={colorDef.paperLighter} elevation={1}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{vt.header.name}</TableCell>
                <TableCell>{vt.header.numLabels}</TableCell>
                <TableCell>{vt.header.status}</TableCell>
                <TableCell>{vt.header.dateCreated}</TableCell>
                <TableCell>{vt.header.dateUpdated}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {modelsWithHandler.map(
                ([
                  model,
                  rowOnClickHandler,
                  editButtonOnClickHandler,
                  deleteButtonOnClickHandler
                ]) => (
                  <MuiTableRow key={model.id} onClick={rowOnClickHandler}>
                    <TableCell>{model.name}</TableCell>
                    <TableCell>{model.numLabels}</TableCell>
                    <TableCell>{model.status}</TableCell>
                    <TableCell>{model.dateCreatedString}</TableCell>
                    <TableCell>{model.dateUpdatedString}</TableCell>
                    <TableCell align='right'>
                      <ButtonWrapper>
                        <MuiButton
                          label={vt.buttons.edit}
                          onClick={editButtonOnClickHandler}
                        />
                      </ButtonWrapper>
                      <ButtonWrapper
                        title={
                          model.canBeDeleted
                            ? undefined
                            : viewTexts.undeletableReason.model
                        }
                      >
                        <MuiButton
                          color='secondary'
                          label={vt.buttons.delete}
                          onClick={deleteButtonOnClickHandler}
                          disabled={!model.canBeDeleted}
                        />
                      </ButtonWrapper>
                    </TableCell>
                  </MuiTableRow>
                )
              )}
            </TableBody>
          </Table>
          <MuiTablePagination
            numRows={numRows}
            page={page}
            onChangePage={onPageChange}
            rowsPerPage={rowsPerPage}
            onChangeRowsPerPage={onRowsPerPageChange}
          />
        </MuiPaper>
        <CreateNewModelDialog
          open={createNewModelDialogOpen}
          onClose={createNewModelDialogOnClose}
          companyId={companyId}
        />
        {modelToEdit !== undefined ? (
          <EditModelDialog
            open={editModelDialogIsOpen}
            onClose={editModelDialogOnClose}
            companyId={companyId}
            model={modelToEdit}
          />
        ) : (
          undefined
        )}
        {modelToDelete !== undefined ? (
          <DeleteModelDialog
            open={deleteModelDialogIsOpen}
            onClose={deleteModelDialogOnClose}
            model={modelToDelete}
            companyId={companyId}
          />
        ) : (
          undefined
        )}
      </Root>
    );
  }
);

ModelListView.displayName = 'ModelListView';
